import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, Cpu, FileSearch, Eye, History, Menu, X, LogOut, Shield, Cable, Fingerprint
} from 'lucide-react';
import { session } from '@/lib/session';
import api from '@/lib/api';

const NAV_ITEMS = [
  { name: 'Overview',    href: '/app/overview',    icon: LayoutDashboard },
  { name: 'Canon',       href: '/app/canon',       icon: BookOpen },
  { name: 'Brain',       href: '/app/brain',       icon: Cpu },
  { name: 'Evidence',    href: '/app/evidence',    icon: FileSearch },
  { name: 'Sentinel',    href: '/app/sentinel',    icon: Eye },
  { name: 'Audit',       href: '/app/audit',       icon: History },
  { name: 'Lifecycle',   href: '/app/lifecycle',   icon: Shield },
  { name: 'Connectors',  href: '/app/connectors',  icon: Cable },
];

const PRIVILEGED_MFA_ROLES = new Set(['ORG_ADMIN', 'CANON_OWNER', 'SECURITY_OPERATOR']);

function getNavItems() {
  const role = session.getUser()?.role;
  if (PRIVILEGED_MFA_ROLES.has(role)) {
    return [...NAV_ITEMS, { name: 'Security', href: '/app/security', icon: Fingerprint }];
  }
  return NAV_ITEMS;
}

function SidebarFooter() {
  const navigate = useNavigate();
  const user = session.getUser();

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (_) {
      // ignore errors — always clear client state
    }
    session.clearAll();
    navigate('/login');
  };

  const displayName = user?.organization_name ?? 'Northstar Organization';
  const roleName = user?.role
    ? user.role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Knowledge Steward';

  return (
    <div className="sidebar-footer-block">
      {/* Tenant + role */}
      <p
        className="font-heading text-white font-medium truncate"
        style={{ fontSize: '13px' }}
        data-testid="tenant-name"
      >
        {displayName}
      </p>
      <p
        className="mt-0.5 truncate"
        style={{ fontSize: '12px', color: '#667085', fontFamily: 'Helvetica, Arial, sans-serif' }}
        data-testid="user-role"
      >
        {roleName}
      </p>

      {/* From DURAHUMAN divider + mark */}
      <div className="from-durahuman-block" aria-label="From Durahuman" data-testid="from-durahuman">
        <img
          src="/images/durahuman-wordmark-transparent.png"
          alt="DURAHUMAN"
          className="durahuman-logo"
          draggable="false"
        />
      </div>

      {/* Sign out */}
      <button
        onClick={handleLogout}
        className="exit-demo-btn"
        aria-label="Sign out"
        data-testid="exit-demo-btn"
      >
        <LogOut size={11} />
        Sign out
      </button>
    </div>
  );
}

function Sidebar({ open, onClose }) {
  const location = useLocation();
  const closeRef = useRef(null);

  // Escape key to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Focus the close button when drawer opens
  useEffect(() => {
    if (open && closeRef.current) closeRef.current.focus();
  }, [open]);

  return (
    <>
      {/* Mobile overlay backdrop */}
      <div
        className={`sidebar-overlay ${open ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sidebar / Drawer */}
      <nav
        className={`kyrdros-sidebar ${open ? 'open' : ''}`}
        data-testid="main-sidebar"
        aria-label="Primary navigation"
        aria-hidden={!open ? 'true' : undefined}
      >
        {/* Header: wordmark + preview label + mobile close */}
        <div className="sidebar-header" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div>
            <span
              className="font-wordmark text-white block leading-none"
              style={{ fontSize: '28px', letterSpacing: '-0.01em' }}
              data-testid="kyrdros-wordmark"
              aria-label="KYRDROS"
            >
              KYRDROS
            </span>
            <span
              className="mt-2 inline-flex items-center px-2 py-0.5 font-heading font-semibold"
              style={{
                color: '#03f37c',
                border: '1px solid rgba(3,243,124,0.3)',
                borderRadius: '2px',
                letterSpacing: '0.06em',
                fontSize: '10px'
              }}
              data-testid="preview-label"
            >
              PREVIEW — SYNTHETIC DATA
            </span>
          </div>
          <button
            ref={closeRef}
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close navigation"
            data-testid="sidebar-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        {/* Navigation links */}
        <div className="flex-1 px-3 py-5 overflow-y-auto">
          {getNavItems().map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href ||
              (item.href !== '/app/overview' && location.pathname.startsWith(item.href));
            return (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={`nav-item mb-0.5 ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.name.toLowerCase()}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon size={15} strokeWidth={1.8} />
                <span>{item.name}</span>
              </NavLink>
            );
          })}
        </div>

        {/* Footer: tenant + From DURAHUMAN */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="px-5 pt-4 pb-5">
            <SidebarFooter />
          </div>
        </div>
      </nav>
    </>
  );
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Desktop sidebar — always visible above 900px */}
      <div className="kyrdros-desktop-sidebar" data-testid="desktop-sidebar">
        <nav
          className="kyrdros-sidebar open"
          aria-label="Primary navigation"
          data-testid="main-sidebar-desktop"
        >
          {/* Desktop header */}
          <div className="sidebar-header" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <div>
              <span
                className="font-wordmark text-white block leading-none"
                style={{ fontSize: '28px', letterSpacing: '-0.01em' }}
                data-testid="kyrdros-wordmark"
                aria-label="KYRDROS"
              >
                KYRDROS
              </span>
              <span
                className="mt-2 inline-flex items-center px-2 py-0.5 font-heading font-semibold"
                style={{
                  color: '#03f37c',
                  border: '1px solid rgba(3,243,124,0.3)',
                  borderRadius: '2px',
                  letterSpacing: '0.06em',
                  fontSize: '10px'
                }}
                data-testid="preview-label"
              >
                PREVIEW — SYNTHETIC DATA
              </span>
            </div>
          </div>

          {/* Desktop nav links */}
          <DesktopNav />

          {/* Desktop footer */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="px-5 pt-4 pb-5">
              <SidebarFooter />
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile drawer overlay */}
      <div className="kyrdros-mobile-drawer">
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Main content */}
      <div className="kyrdros-main">
        {/* Mobile top bar */}
        <header
          className="kyrdros-mobile-topbar"
          data-testid="mobile-topbar"
        >
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white focus:outline-none"
            aria-label="Open navigation"
            data-testid="sidebar-open-btn"
          >
            <Menu size={22} />
          </button>
          <span
            className="font-wordmark text-white leading-none"
            style={{ fontSize: '22px' }}
          >
            KYRDROS
          </span>
          <span
            className="font-heading font-medium"
            style={{ color: '#03f37c', letterSpacing: '0.04em', fontSize: '10px' }}
          >
            PREVIEW
          </span>
        </header>

        {/* Page content */}
        <main
          className="ky-page-main"
          data-testid="main-content"
        >
          <div style={{ maxWidth: '1480px', margin: '0 auto' }}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function DesktopNav() {
  const location = useLocation();
  return (
    <div className="flex-1 px-3 py-5 overflow-y-auto">
      {getNavItems().map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.href ||
          (item.href !== '/app/overview' && location.pathname.startsWith(item.href));
        return (
          <NavLink
            key={item.name}
            to={item.href}
            className={`nav-item mb-0.5 ${isActive ? 'active' : ''}`}
            data-testid={`nav-desktop-${item.name.toLowerCase()}`}
            aria-current={isActive ? 'page' : undefined}
          >
            <Icon size={15} strokeWidth={1.8} />
            <span>{item.name}</span>
          </NavLink>
        );
      })}
    </div>
  );
}
